<script lang="ts">
	import { format } from 'svelte-i18n';
	import { ButtonV2, DonutChart, LiveIndicator, Text } from '@Shelly/shelly-design-system';
	import { push } from 'svelte-spa-router';
	import { devices } from 'src/stores';
	import {
		consumption_by_device_account,
		consumption_device,
		consumption_rooms,
	} from 'src/stores/features/consumption';
	import { rooms } from 'src/stores';
	import { capFirstChar, convertValueAndUnit } from 'src/helpers/utilities';
	import { getApplianceTypeByUsage } from 'src/helpers/device_settings_helpers';
	import { buildConsumptionStores } from 'src/components/EnergyMonitoring/helpers/consumption_helpers';
	import { buildApplianceBreakdown } from 'src/components/EnergyMonitoring/helpers/energy_flow_helpers';
	import { toPieChartData } from 'src/components/EnergyMonitoring/helpers/layer_chart_adapter';
	import {
		CONSUMER_MIX_COLORS,
		DEMO_CONSUMER_MIX,
	} from 'src/components/EnergyMonitoring/helpers/energy_demo_data';
	import { energyMonitoringDataMode } from 'src/components/EnergyMonitoring/helpers/store';
	import EmWidgetShell from '../EmWidgetShell.svelte';
	import EmLayerChartFrame from '../EmLayerChartFrame.svelte';
	import EmWidgetState from '../EmWidgetState.svelte';
	import { getEnergyChartEmptyMessage } from '../helpers/energy_flow_helpers';
	import {
		ENERGY_ADD_DEVICE_HREF,
		hasPowerMeterDevices,
	} from '../helpers/energy_widget_state_helpers';

	export let embedded = false;
	export let showLegend = true;
	export let minSharePercent = 0;
	export let staticPreview = false;
	export let showClassificationAction = false;

	$: consumptionStores = buildConsumptionStores({
		rooms: $rooms,
		devices: $devices,
		consumptionRooms: $consumption_rooms,
		consumptionDevice: $consumption_device,
		consumptionByDeviceAccount: $consumption_by_device_account,
	});

	$: breakdown = buildApplianceBreakdown(consumptionStores, $devices);
	$: visibleBreakdown = (() => {
		if (minSharePercent <= 0 || breakdown.total <= 0) return breakdown;
		const kept: { key: string; value: number }[] = [];
		let other = 0;
		breakdown.keys.forEach((key, index) => {
			const value = breakdown.values[index];
			if (key === 'other') other += value;
			else if ((value / breakdown.total) * 100 >= minSharePercent) kept.push({ key, value });
			else other += value;
		});
		if (other > 0) kept.push({ key: 'other', value: other });
		return {
			keys: kept.map((item) => item.key),
			values: kept.map((item) => item.value),
			total: breakdown.total,
		};
	})();

	$: useDemoData = staticPreview || $energyMonitoringDataMode === 'demo';
	$: hasLiveBreakdown = visibleBreakdown.values.length > 0 && visibleBreakdown.total > 0;

	$: liveLegendItems = (() => {
		if (useDemoData || !hasLiveBreakdown) {
			if (!useDemoData) return [];
			return DEMO_CONSUMER_MIX.keys.map((key, index) => {
				const { value, unit } = convertValueAndUnit(DEMO_CONSUMER_MIX.values[index], 'W');
				return {
					id: key,
					label: DEMO_CONSUMER_MIX.labels[index],
					valueLabel: `${value} ${unit}`,
					color: CONSUMER_MIX_COLORS[index % CONSUMER_MIX_COLORS.length],
				};
			});
		}

		return visibleBreakdown.keys.map((key, index) => {
			const type = getApplianceTypeByUsage(key);
			const { value, unit } = convertValueAndUnit(visibleBreakdown.values[index], 'W');
			return {
				id: key,
				label: capFirstChar(type.title),
				valueLabel: `${value} ${unit}`,
				color: type.chartColor ?? CONSUMER_MIX_COLORS[index % CONSUMER_MIX_COLORS.length],
			};
		});
	})();

	$: liveChartData = (() => {
		if (!liveLegendItems.length) return null;

		if (useDemoData || !hasLiveBreakdown) {
			return toPieChartData({
				labels: DEMO_CONSUMER_MIX.labels,
				keys: DEMO_CONSUMER_MIX.keys,
				values: DEMO_CONSUMER_MIX.values,
				colors: liveLegendItems.map((item) => item.color),
			});
		}

		return toPieChartData({
			labels: liveLegendItems.map((item) => item.label),
			keys: visibleBreakdown.keys,
			values: visibleBreakdown.values,
			colors: liveLegendItems.map((item) => item.color),
		});
	})();

	let previewLegendItems: typeof liveLegendItems | undefined;
	let previewChartData: typeof liveChartData | undefined;
	$: legendItems = staticPreview
		? (previewLegendItems ?? (previewLegendItems = liveLegendItems))
		: liveLegendItems;
	$: chartData = staticPreview
		? (previewChartData ?? (previewChartData = liveChartData))
		: liveChartData;
	$: unknownLegendItem = !useDemoData ? legendItems.find((item) => item.id === 'unknown') : undefined;
	$: emptyMessage = getEnergyChartEmptyMessage($devices, (key, fallback) =>
		$format(key, { default: fallback }),
	);
</script>

{#if embedded}
	{#if chartData}
		<div class="energy-donut-wrap">
			<div class="energy-chart-block">
				<div class="energy-donut-chart">
					<EmLayerChartFrame height={280} variant="pie" reserveLegendSlot={false}>
						<DonutChart
							{chartData}
							centerLabel={$format('label_live', { default: 'Live' })}
							showCenterSummary
							{showLegend}
						/>
					</EmLayerChartFrame>
				</div>
			</div>
			{#if showClassificationAction && unknownLegendItem}
				<div class="consumer-mix-classification" data-dashboard-widget-interactive>
					<Text styleType="body-small" class="text-color-muted">
						{$format('energy_monitoring_consumer_mix_unknown_message', {
							default:
								'{value} is not assigned to an appliance category. Review meter assignments to improve this breakdown.',
							values: { value: unknownLegendItem.valueLabel },
						})}
					</Text>
					<ButtonV2
						size="s"
						variant="secondary"
						on:click={() => push('/home/devices/-1?appliance_type=unknown')}
					>
						{$format('energy_monitoring_consumer_mix_review_meters', {
							default: 'Review meters',
						})}
					</ButtonV2>
				</div>
			{/if}
		</div>
	{:else}
		<EmWidgetState
			kind={hasPowerMeterDevices($devices) ? 'empty' : 'setup'}
			message={emptyMessage}
			minHeight="180px"
			actionHref={!hasPowerMeterDevices($devices) ? ENERGY_ADD_DEVICE_HREF : ''}
			actionLabel={!hasPowerMeterDevices($devices)
				? $format('button_add_device', { default: 'Add device' })
				: ''}
		/>
	{/if}
{:else}
	<EmWidgetShell
		widgetClass="energy-widget-donut"
		title={$format('energy_monitoring_consumer_mix_title', { default: 'Consumer mix' })}
		infoText={$format('energy_monitoring_consumer_mix_info', {
			default: 'Share of live power by appliance category (heating, EV, kitchen, etc.).',
		})}
	>
		<svelte:fragment slot="actions">
			<LiveIndicator />
		</svelte:fragment>
		<div class="energy-donut-wrap">
			{#if chartData}
				<div class="energy-chart-block">
					<div class="energy-donut-chart">
						<EmLayerChartFrame height={280} variant="pie" reserveLegendSlot={false}>
							<DonutChart
								{chartData}
								centerLabel={$format('label_live', { default: 'Live' })}
								showCenterSummary
								{showLegend}
							/>
						</EmLayerChartFrame>
					</div>
				</div>
			{:else}
				<EmWidgetState
					kind={hasPowerMeterDevices($devices) ? 'empty' : 'setup'}
					message={emptyMessage}
					minHeight="180px"
					actionHref={!hasPowerMeterDevices($devices) ? ENERGY_ADD_DEVICE_HREF : ''}
					actionLabel={!hasPowerMeterDevices($devices)
						? $format('button_add_device', { default: 'Add device' })
						: ''}
				/>
			{/if}
		</div>
	</EmWidgetShell>
{/if}

<style>
	.energy-donut-wrap {
		display: flex;
		flex-direction: column;
		flex: 1;
		width: 100%;
		min-width: 0;
		min-height: 0;
		container-type: inline-size;
		container-name: consumer-mix;
	}

	.energy-chart-block {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--spacing-s);
		flex: 1;
		min-width: 0;
		min-height: 0;
	}

	.energy-donut-chart {
		flex: 1;
		width: 100%;
		min-width: 0;
		min-height: 280px;
		overflow: visible;
	}

	.energy-donut-chart :global(.lc-pie-visual) {
		max-width: min(100%, 220px);
	}

	.consumer-mix-classification {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacing-m);
		padding: var(--spacing-s) var(--spacing-m) var(--spacing-m);
	}

	.consumer-mix-classification :global(.text) {
		min-width: 0;
	}

	@container consumer-mix (max-width: 420px) {
		.consumer-mix-classification {
			align-items: stretch;
			flex-direction: column;
		}
	}

	@container consumer-mix (max-width: 520px) {
		.energy-donut-chart {
			width: 100%;
			margin: 0 auto;
		}
	}
</style>
